import { TrendingUp, TrendingDown, Minus, HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

const KPICard = ({
  title,
  value,
  benchmark,
  format = "percent",
  previousValue,
  showBenchmark = true,
  className = "",
  tooltip = "",
}) => {
  const formatValue = (val) => {
    // Handle null, undefined, or NaN values
    if (val === null || val === undefined || isNaN(val)) {
      if (format === "percent") return "0.0%";
      if (format === "currency") return "$0";
      if (format === "hours") return "0.0 hrs";
      return "0.0";
    }
    if (format === "percent") {
      return `${val.toFixed(1)}%`;
    }
    if (format === "currency") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(val);
    }
    if (format === "hours") {
      return `${val.toFixed(1)} hrs`;
    }
    return val.toFixed(1);
  };

  const getStatus = () => {
    if (!showBenchmark || benchmark === undefined || value === null || value === undefined || isNaN(value)) return "neutral";
    const ratio = value / benchmark;
    if (ratio >= 1) return "success";
    if (ratio >= 0.85) return "warning";
    return "danger";
  };

  const status = getStatus();

  const getTrend = () => {
    if (previousValue === undefined || previousValue === null || previousValue === 0) return null;
    if (value === undefined || value === null || isNaN(value)) return null;
    const change = ((value - previousValue) / previousValue) * 100;
    if (Math.abs(change) < 0.5) return { direction: "flat", value: 0 };
    return {
      direction: change > 0 ? "up" : "down",
      value: Math.abs(change).toFixed(1),
    };
  };

  const trend = getTrend();

  const statusColors = {
    success: {
      bg: "bg-emerald-500/20",
      text: "text-emerald-400",
      progress: "bg-emerald-500",
    },
    warning: {
      bg: "bg-amber-500/20",
      text: "text-amber-400",
      progress: "bg-amber-500",
    },
    danger: {
      bg: "bg-rose-500/20",
      text: "text-rose-400",
      progress: "bg-rose-500",
    },
    neutral: {
      bg: "bg-muted",
      text: "text-muted-foreground",
      progress: "bg-blue-500",
    },
  };

  const colors = statusColors[status];
  const safeValue = (value === null || value === undefined || isNaN(value)) ? 0 : value;
  const progressValue = showBenchmark && benchmark > 0 
    ? Math.min((safeValue / benchmark) * 100, 100) 
    : 0;

  return (
    <div
      className={`kpi-card stat-card ${className}`}
      data-testid={`kpi-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-1">
          <span className="stat-label">{title}</span>
          {tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle size={14} className="text-muted-foreground hover:text-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-sm">{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {trend && (
          <div
            className={`flex items-center gap-1 text-xs font-medium ${
              trend.direction === "up"
                ? "text-emerald-400"
                : trend.direction === "down"
                ? "text-rose-400"
                : "text-muted-foreground"
            }`}
          >
            {trend.direction === "up" ? (
              <TrendingUp size={14} />
            ) : trend.direction === "down" ? (
              <TrendingDown size={14} />
            ) : (
              <Minus size={14} />
            )}
            <span>{trend.value}%</span>
          </div>
        )}
      </div>

      <div className="stat-value text-foreground mb-2">{formatValue(value)}</div>

      {showBenchmark && benchmark !== undefined && (
        <>
          <div className="mb-3">
            <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${colors.progress}`}
                style={{ width: `${progressValue}%` }}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Target: {formatValue(benchmark)}
            </span>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}
            >
              {status === "success"
                ? "On Track"
                : status === "warning"
                ? "Close"
                : "Below"}
            </span>
          </div>
        </>
      )}

      {previousValue !== undefined && (
        <div className="mt-3 pt-3 border-t border-border">
          <span className="text-xs text-muted-foreground">
            Previous YTD: {formatValue(previousValue)}
          </span>
        </div>
      )}
    </div>
  );
};

export default KPICard;
